import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import AddServices from './AddServices';
import constants from './constants';
import BaseDialog from './BaseDialog';
import getUrlPrefix from './utilities/getUrlPrefix';
import axios from './axios';
import { notify } from './Notification';
import filterEmpty from './utilities/filterEmpty';
import formatDateForRequest from './utilities/formatDateForRequest';
import { isValidDate } from './utilities';
import type { Service, ClaimExtract } from './AddServices.types';

const minBenefitDate = dayjs('01-01-1970');

interface AddServicesDialogProps {
  usesServiceConverting?: boolean;
  editedService?: Service;
  open: boolean;
  onClose: () => void;
  patientId?: string;
  inOfficePlanId?: string;
  addService: (service: Service | ClaimExtract, addAnother?: boolean) => Promise<unknown>;
  editService: (service: Service | ClaimExtract) => void;
  dentalAccessPoint?: string;
  date?: string;
  practice?: string;
  practiceType: string;
  portal?: string;
  showFrequency?: boolean;
  noSettlement?: boolean;
  requireOfficeFee?: boolean;
}

function AddServicesDialog({
  usesServiceConverting,
  editedService,
  open,
  onClose,
  patientId,
  inOfficePlanId,
  addService,
  editService,
  dentalAccessPoint,
  date,
  practice,
  practiceType,
  portal = constants.PORTALS.DENTIST,
  showFrequency = false,
  noSettlement = false,
  requireOfficeFee = false,
}: AddServicesDialogProps) {
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState<boolean>(false);

  const fetchServices = () => {
    const allServicesUrlMap: Record<string, string> = {
      [constants.PORTALS.ADMIN]: 'mast-alto/dental-service/',
      [constants.PORTALS.DENTIST]: 'docs/all-dental-services/',
    };

    if (noSettlement) {
      const url = `${allServicesUrlMap[portal]}?type=${practiceType}`;
      return axios.get(url);
    }

    const body =
      practiceType === constants.NETWORK_TYPES.OUT_OF_NETWORK
        ? { type: practiceType }
        : filterEmpty({
            dental_access_point: dentalAccessPoint,
            date: formatDateForRequest(date),
            patient: patientId,
            in_office_plan: inOfficePlanId,
            practice,
            type: practiceType,
          });

    const params = new URLSearchParams();
    Object.entries(body).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params.append(key, String(value));
      }
    });

    const url = `${getUrlPrefix(portal)}/dental-service/?${params.toString()}`;

    return axios.get(url);
  };
  useEffect(() => {
    const shouldFetchServices = (currentPracticeType: string): boolean => {
      if (currentPracticeType === constants.NETWORK_TYPES.OUT_OF_NETWORK) {
        return true;
      }
      return !!dentalAccessPoint && (!!patientId || !!inOfficePlanId);
    };

    const isDateCorrect = (currentPracticeType: string): boolean => {
      if (currentPracticeType === constants.NETWORK_TYPES.OUT_OF_NETWORK) {
        return true;
      }
      return isValidDate(date) && !dayjs(date).isBefore(minBenefitDate);
    };

    const canFetchServices = shouldFetchServices(practiceType);
    const dateIsCorrect = isDateCorrect(practiceType);

    if (open && canFetchServices && dateIsCorrect) {
      setServicesLoading(true);
      fetchServices()
        .then((response) => {
          setAllServices(response.data.data.services);
          setServicesLoading(false);
        })
        .catch((error: Error) => {
          setServicesLoading(false);
          notify(error.message, 'error');
        });
    }
  }, [open, practiceType, dentalAccessPoint, patientId, inOfficePlanId, date, practice]);

  const shouldShowFrequency = portal === constants.PORTALS.ADMIN ? !noSettlement : showFrequency;

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      fullWidth={false}
      maxWidth="lg"
      title={editedService ? 'Edit Service' : 'Add Service'}
      showCloseButton
    >
      <AddServices
        allServices={allServices}
        servicesLoading={servicesLoading}
        usesServiceConverting={usesServiceConverting}
        editedService={editedService}
        patient={patientId}
        dentalAccessPoint={dentalAccessPoint}
        addService={addService}
        editService={editService}
        handleClose={onClose}
        date={date}
        portal={portal}
        showFrequency={shouldShowFrequency}
        requireOfficeFee={requireOfficeFee}
        isInDialog
      />
    </BaseDialog>
  );
}

export default AddServicesDialog;
